import { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Button, Popconfirm, Select, Typography, message, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useList } from '@refinedev/core';
import type { Company } from '@viq-admin/types';
import {
  listTemplateCompanies,
  detachTemplate,
  reapplyTemplate,
  applyTemplate,
} from '../../api/promptStore';
import { Can } from '../../ability/AbilityContext';
import { space } from '@viq-admin/ui';

/** A company this template's append is applied to (list row shape). */
interface AppliedCompany {
  companyId: string;
  name: string;
}

/**
 * Modal listing every company this prompt template's append is applied to,
 * with per-row detach and a "Reapply to all" action — PLUS (above that
 * table) the "Apply to companies" section that actually applies the
 * template to new companies. Mirrors
 * `toolkits/AppliedConferencesModal.tsx` with one important divergence:
 *
 * Backend reality (`api/promptStore`):
 *  - `listTemplateCompanies(templateId)` returns `{ companyId, name }[]`
 *    (NOT full `Company[]` rows) — rows are keyed/detached on `companyId`,
 *    not `id`.
 *  - `applyTemplate(templateId, companyIds)` applies to ALL selected
 *    companies in ONE batched call and resolves to the count applied — the
 *    multi-select does NOT loop a per-id call (contrast the conferences
 *    modal's `Promise.all(ids.map(applyConfig...))`).
 *  - `reapplyTemplate(templateId)` resolves to a plain `number` (NOT
 *    `{ count }` like `reapplyConfig`).
 *
 * Prompt templates are global/cross-tenant, so the "all companies" fetch
 * below passes `meta: { companyId: null }` to explicitly bypass the active
 * "operate as tenant" scope (dataProvider.getList treats an explicit
 * `companyId` key — including `null` — as an override; see dataProvider.ts).
 * Omitting the key would silently narrow the picker to whatever tenant the
 * operator currently happens to be scoped to.
 *
 * Mutating buttons (detach / reapply / apply) are gated on
 * `manage PromptTemplate`; a read-only super admin sees the company list
 * but no write controls.
 */
interface Props {
  templateId: string;
  open: boolean;
  onClose: () => void;
}

export function AppliedCompaniesModal({ templateId, open, onClose }: Props) {
  const [rows, setRows] = useState<AppliedCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [reapplying, setReapplying] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);

  const { result: companiesResult, query: companiesQuery } = useList<Company>({
    resource: 'companies',
    // Force the cross-tenant union regardless of the active "operate as"
    // scope — see the file header comment for why.
    meta: { companyId: null },
  });

  const appliedIds = useMemo(() => new Set(rows.map((c) => c.companyId)), [rows]);
  const unappliedCompanies = useMemo(
    () => (companiesResult?.data ?? []).filter((c) => !appliedIds.has(c.id)),
    [companiesResult, appliedIds],
  );

  function refreshApplied() {
    return listTemplateCompanies(templateId).then(setRows);
  }

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listTemplateCompanies(templateId)
      .then(setRows)
      .catch((err) =>
        message.error((err as { message?: string }).message ?? 'Failed to load'),
      )
      .finally(() => setLoading(false));
  }, [templateId, open]);

  async function handleApply() {
    setApplying(true);
    try {
      const targetIds = selected;
      const count = await applyTemplate(templateId, targetIds);
      await refreshApplied();
      setSelected([]);
      message.success(`Applied to ${count} company(ies)`);
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Apply failed');
    } finally {
      setApplying(false);
    }
  }

  async function handleDetach(companyId: string) {
    try {
      await detachTemplate(templateId, companyId);
      setRows((prev) => prev.filter((c) => c.companyId !== companyId));
      message.success('Detached');
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Detach failed');
    }
  }

  async function handleReapply() {
    setReapplying(true);
    try {
      const n = await reapplyTemplate(templateId);
      message.success(`Reapplied to ${n} company(ies)`);
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Reapply failed');
    } finally {
      setReapplying(false);
    }
  }

  const columns: ColumnsType<AppliedCompany> = [
    { dataIndex: 'name', key: 'name', title: 'Company' },
    {
      key: 'actions',
      title: 'Actions',
      width: 100,
      render: (_, record) => (
        <Can I="manage" a="PromptTemplate" passThrough>
          {(allowed: boolean) =>
            allowed ? (
              <Popconfirm
                title="Detach this company?"
                onConfirm={() => handleDetach(record.companyId)}
              >
                <Button type="link" danger size="small">
                  Detach
                </Button>
              </Popconfirm>
            ) : null
          }
        </Can>
      ),
    },
  ];

  return (
    <Modal
      title="Applied companies"
      open={open}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Can key="reapply" I="manage" a="PromptTemplate" passThrough>
          {(allowed: boolean) =>
            allowed ? (
              <Popconfirm
                title="Push updates to all applied companies?"
                onConfirm={handleReapply}
              >
                <Button type="primary" loading={reapplying}>
                  Reapply to all
                </Button>
              </Popconfirm>
            ) : null
          }
        </Can>,
      ]}
    >
      <Can I="manage" a="PromptTemplate" passThrough>
        {(allowed: boolean) =>
          allowed ? (
            <div style={{ marginBottom: space.xl }}>
              <Typography.Text strong>Apply to companies</Typography.Text>
              <div style={{ display: 'flex', gap: space.sm, marginTop: space.sm }}>
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  style={{ flex: 1 }}
                  placeholder="Select companies to apply this template to"
                  data-testid="apply-companies-select"
                  value={selected}
                  onChange={setSelected}
                  loading={companiesQuery?.isLoading}
                  options={unappliedCompanies.map((c) => ({
                    label: c.name,
                    value: c.id,
                  }))}
                />
                {/* Apply overwrites `append_body` unconditionally server-side.
                    The picker only excludes companies applied to THIS template,
                    so a company carrying a DIFFERENT template's append is
                    offered here and would be silently replaced — confirm first. */}
                <Popconfirm
                  title={`Apply this template to ${selected.length} ${selected.length === 1 ? 'company' : 'companies'}?`}
                  description="Any of them that already have custom rules for this pipeline will have them replaced."
                  onConfirm={handleApply}
                >
                  <Button type="primary" loading={applying} disabled={selected.length === 0}>
                    Apply
                  </Button>
                </Popconfirm>
              </div>
            </div>
          ) : null
        }
      </Can>
      {loading ? (
        <Spin />
      ) : (
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="companyId"
          pagination={false}
        />
      )}
    </Modal>
  );
}
